
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import { blogPosts, blogCategories } from '../../mocks/blogPosts';

const POSTS_PER_PAGE = 6;
const BASE_URL = 'https://alliancesecuritysystems.com';

export default function BlogPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [serviceAreasOpen, setServiceAreasOpen] = useState(false);
  const [mobileServiceAreasOpen, setMobileServiceAreasOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery]);

  const serviceAreas = [
    { name: 'Toronto', path: '/service-areas/toronto' },
    { name: 'Mississauga', path: '/service-areas/mississauga' },
    { name: 'Brampton', path: '/service-areas/brampton' },
    { name: 'Oakville', path: '/service-areas/oakville' },
    { name: 'Hamilton', path: '/service-areas/hamilton' },
    { name: 'Richmond Hill', path: '/service-areas/richmond-hill' },
    { name: 'Vaughan', path: '/service-areas/vaughan' },
  ];

  const featuredPost = blogPosts.find((p) => p.featured) ?? blogPosts[0];

  const filteredPosts = blogPosts.filter((post) => {
    const matchesCategory =
      selectedCategory === 'All' || post.category === selectedCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      post.title.toLowerCase().includes(q) ||
      post.excerpt.toLowerCase().includes(q) ||
      post.tags.some((t) => t.toLowerCase().includes(q)) ||
      post.category.toLowerCase().includes(q);
    return matchesCategory && matchesSearch;
  });

  const nonFeaturedFiltered =
    selectedCategory === 'All' && !searchQuery
      ? filteredPosts.filter((p) => p.id !== featuredPost.id)
      : filteredPosts;

  const totalPages = Math.ceil(nonFeaturedFiltered.length / POSTS_PER_PAGE);
  const paginatedPosts = nonFeaturedFiltered.slice(
    (currentPage - 1) * POSTS_PER_PAGE,
    currentPage * POSTS_PER_PAGE
  );

  const categoryColors: Record<string, string> = {
    'Security Tips': 'bg-emerald-100 text-emerald-700',
    'Product Reviews': 'bg-amber-100 text-amber-700',
    'Industry News': 'bg-sky-100 text-sky-700',
    'Case Studies': 'bg-rose-100 text-rose-700',
    'How-To Guides': 'bg-violet-100 text-violet-700',
  };

  const clearFilters = () => {
    setSelectedCategory('All');
    setSearchQuery('');
  };

  // --- Structured Data for AI & Search Engines ---
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: BASE_URL,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: `${BASE_URL}/blog`,
      },
    ],
  };

  const blogSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    '@id': `${BASE_URL}/blog#blog`,
    name: 'Alliance Security Systems Blog',
    description:
      'Expert security tips, product reviews, case studies, and industry news from Alliance Security Systems. Stay informed about the latest in home and commercial security across the GTA.',
    url: `${BASE_URL}/blog`,
    publisher: {
      '@type': 'Organization',
      name: 'Alliance Security Systems',
      url: BASE_URL,
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/logo.png`,
      },
    },
    blogPost: blogPosts.map((post) => ({
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.excerpt,
      url: `${BASE_URL}/blog/${post.slug}`,
      image: post.image,
      datePublished: post.date,
      author: {
        '@type': 'Person',
        name: post.author,
        jobTitle: post.authorRole,
      },
      keywords: post.tags.join(', '),
      articleSection: post.category,
    })),
  };

  const collectionPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Security Blog — Tips, Reviews & Industry News',
    description:
      'Expert security tips, product reviews, case studies, and industry news from Alliance Security Systems. Stay informed about the latest in home and commercial security across the GTA.',
    url: `${BASE_URL}/blog`,
    isPartOf: {
      '@type': 'WebSite',
      name: 'Alliance Security Systems',
      url: BASE_URL,
    },
    about: {
      '@type': 'Thing',
      name: 'Security Systems',
      description:
        'Home and commercial security systems, alarm systems, security cameras, access control, and live monitoring.',
    },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: blogPosts.length,
      itemListElement: blogPosts.map((post, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `${BASE_URL}/blog/${post.slug}`,
        name: post.title,
      })),
    },
  };

  const structuredData = [breadcrumbSchema, blogSchema, collectionPageSchema];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Security Blog — Tips, Reviews & Industry News | Alliance Security Systems"
        description="Expert security tips, product reviews, case studies, and industry news from Alliance Security Systems. Stay informed about the latest in home and commercial security across the GTA."
        keywords="security blog, home security tips, security camera reviews, alarm system guide, GTA security news, commercial security, Mississauga security blog"
        canonical="/blog"
        structuredData={structuredData}
      />

      {/* Navigation */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-md'
        }`}
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex flex-col leading-none border-2 border-[#D4A853] rounded-lg px-4 py-2.5 bg-[#0A1628]">
              <span
                className="text-2xl sm:text-3xl font-extrabold tracking-wider text-[#D4A853]"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                ALLIANCE
              </span>
              <span className="text-[10px] sm:text-xs font-semibold tracking-[0.25em] text-[#D4A853]">
                SECURITY SYSTEMS
              </span>
            </div>
          </Link>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-gray-700 cursor-pointer"
          >
            <i
              className={`${
                mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
              } text-2xl`}
            ></i>
          </button>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/camera-packages"
              className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
            >
              Cameras
            </Link>
            <Link
              to="/access-control"
              className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
            >
              Access Control
            </Link>
            <Link
              to="/alarm-packages"
              className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
            >
              Alarm Packages
            </Link>
            <Link
              to="/live-monitoring"
              className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
            >
              Live Monitoring
            </Link>
            <Link
              to="/clients"
              className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
            >
              Clients
            </Link>
            <Link
              to="/faq"
              className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
            >
              FAQ
            </Link>
            <Link
              to="/blog"
              className="text-sm font-medium text-[#D4A853] transition-colors cursor-pointer"
            >
              Blog
            </Link>
            <Link
              to="/contact"
              className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer"
            >
              Contact Us
            </Link>

            <div
              className="relative"
              onMouseEnter={() => setServiceAreasOpen(true)}
              onMouseLeave={() => setServiceAreasOpen(false)}
            >
              <button className="text-sm font-medium text-gray-700 hover:text-[#D4A853] transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap">
                Service Areas
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    serviceAreasOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {serviceAreasOpen && (
                <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                  {serviceAreas.map((area) => (
                    <Link
                      key={area.path}
                      to={area.path}
                      className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#D4A853]/10 hover:text-[#D4A853] transition-colors cursor-pointer"
                    >
                      {area.name}
                    </Link>
                  ))}
                  <div className="border-t border-gray-100 mt-1 pt-1">
                    <Link
                      to="/service-areas"
                      className="block px-4 py-2.5 text-sm font-semibold text-[#D4A853] hover:bg-[#D4A853]/10 transition-colors cursor-pointer"
                    >
                      View All Areas →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/contact"
              className="bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
            >
              Get Free Quote
            </Link>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-2">
            <Link
              to="/camera-packages"
              className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"
            >
              Camera Packages
            </Link>
            <Link
              to="/access-control"
              className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"
            >
              Access Control
            </Link>
            <Link
              to="/alarm-packages"
              className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"
            >
              Alarm Packages
            </Link>
            <Link
              to="/live-monitoring"
              className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"
            >
              Live Monitoring
            </Link>
            <Link
              to="/clients"
              className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"
            >
              Clients
            </Link>
            <Link
              to="/faq"
              className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"
            >
              FAQ
            </Link>
            <Link
              to="/blog"
              className="block py-3 text-sm font-medium text-[#D4A853] border-b border-gray-50"
            >
              Blog
            </Link>
            <Link
              to="/contact"
              className="block py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] border-b border-gray-50"
            >
              Contact Us
            </Link>
            <div className="border-b border-gray-50">
              <button
                onClick={() =>
                  setMobileServiceAreasOpen(!mobileServiceAreasOpen)
                }
                className="w-full flex items-center justify-between py-3 text-sm font-medium text-gray-700 hover:text-[#D4A853] cursor-pointer"
              >
                Service Areas
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    mobileServiceAreasOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {mobileServiceAreasOpen && (
                <div className="pl-4 pb-3 space-y-1">
                  {serviceAreas.map((area) => (
                    <Link
                      key={area.path}
                      to={area.path}
                      className="block py-2 text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer"
                    >
                      {area.name}
                    </Link>
                  ))}
                  <Link
                    to="/service-areas"
                    className="block py-2 text-sm font-semibold text-[#D4A853] hover:text-[#B8923D] transition-colors cursor-pointer"
                  >
                    View All Areas →
                  </Link>
                </div>
              )}
            </div>
            <Link
              to="/contact"
              className="block w-full text-center bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap mt-2"
            >
              Get Free Quote
            </Link>
          </div>
        )}
      </nav>

      {/* Breadcrumb Navigation */}
      <nav
        className="max-w-7xl mx-auto px-4 sm:px-6 pt-4"
        aria-label="Breadcrumb"
      >
        <ol
          className="flex items-center gap-2 text-xs text-gray-500"
          itemScope
          itemType="https://schema.org/BreadcrumbList"
        >
          <li
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            <Link
              to="/"
              className="hover:text-[#D4A853] transition-colors cursor-pointer"
              itemProp="item"
            >
              <span itemProp="name">Home</span>
            </Link>
            <meta itemProp="position" content="1" />
          </li>
          <li>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
          </li>
          <li
            itemProp="itemListElement"
            itemScope
            itemType="https://schema.org/ListItem"
          >
            <span className="text-[#D4A853] font-medium" itemProp="name">
              Blog
            </span>
            <meta itemProp="position" content="2" />
          </li>
        </ol>
      </nav>

      {/* Hero / Featured Post */}
      <header
        className="relative bg-[#0A1628] overflow-hidden mt-4"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(24px)',
          transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}
      >
        <div className="absolute inset-0">
          <img
            src={featuredPost.image}
            alt={`Featured article: ${featuredPost.title} - Alliance Security Systems Blog`}
            title={`${featuredPost.title} - Security Tips GTA`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/92 via-[#0A1628]/75 to-[#0A1628]/30"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28 md:py-36">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="bg-[#D4A853] text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide whitespace-nowrap">
                Featured
              </span>
              <span className="text-white/70 text-sm">{featuredPost.category}</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white leading-tight mb-5">
              {/* Switched from <a> to <Link> to avoid JSX parsing errors */}
              <Link
                to={`/blog/${featuredPost.slug}`}
                className="hover:text-[#D4A853] transition-colors"
              >
                {featuredPost.title}
              </Link>
            </h1>
            <p className="text-white/80 text-base sm:text-lg mb-8 leading-relaxed speakable">
              {featuredPost.excerpt}
            </p>
            <div className="flex items-center gap-5 mb-8">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                <img
                  src={featuredPost.authorImage}
                  alt={featuredPost.author}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <p className="text-white text-sm font-semibold">
                  {featuredPost.author}
                </p>
                <p className="text-white/60 text-xs">
                  <time dateTime={new Date(featuredPost.date).toISOString().split('T')[0]}>
                    {featuredPost.date}
                  </time>{' '}
                  &middot; {featuredPost.readTime} min read
                </p>
              </div>
            </div>
            <Link
              to={`/blog/${featuredPost.slug}`}
              className="inline-flex items-center gap-3 bg-[#D4A853] text-white px-7 py-3.5 rounded-full font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg"
            >
              Read Article
              <i className="ri-arrow-right-line text-lg"></i>
            </Link>
          </div>
        </div>
      </header>

      {/* Search + Filter Bar */}
      <section
        className="bg-white border-b border-gray-100 sticky top-[72px] z-40 shadow-sm"
        aria-label="Blog filters"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-base"></i>
            <input
              type="text"
              placeholder="Search articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] bg-[#F8F9FA]"
              aria-label="Search blog articles"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                aria-label="Clear search"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            )}
          </div>
          <div
            className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 flex-nowrap"
            role="tablist"
            aria-label="Blog categories"
          >
            {blogCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                role="tab"
                aria-selected={selectedCategory === cat}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                  selectedCategory === cat
                    ? 'bg-[#D4A853] text-white shadow-md'
                    : 'bg-[#F8F9FA] text-gray-600 hover:bg-[#D4A853]/10 hover:text-[#D4A853] border border-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
        <div className="flex items-center justify-between mb-8">
          <p className="text-sm text-gray-500">
            {nonFeaturedFiltered.length === 0
              ? 'No articles found'
              : `Showing ${nonFeaturedFiltered.length} article${
                  nonFeaturedFiltered.length !== 1 ? 's' : ''
                }${selectedCategory !== 'All' ? ` in "${selectedCategory}"` : ''}`}
          </p>
          {(selectedCategory !== 'All' || searchQuery) && (
            <button
              onClick={clearFilters}
              className="text-xs text-[#D4A853] hover:underline cursor-pointer flex items-center gap-1"
            >
              <i className="ri-refresh-line"></i> Clear filters
            </button>
          )}
        </div>

        {paginatedPosts.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-20 h-20 bg-[#F8F9FA] rounded-full flex items-center justify-center mx-auto mb-5">
              <i className="ri-search-line text-3xl text-gray-400"></i>
            </div>
            <h3 className="text-xl font-bold text-[#0A1628] mb-2">
              No articles found
            </h3>
            <p className="text-gray-500 text-sm mb-6">
              Try a different search term or category.
            </p>
            <button
              onClick={clearFilters}
              className="bg-[#D4A853] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
            >
              View All Articles
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {paginatedPosts.map((post, idx) => (
              <article
                key={post.id}
                className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 flex flex-col"
                itemScope
                itemType="https://schema.org/BlogPosting"
                style={{
                  opacity: visible ? 1 : 0,
                  transform: visible ? 'translateY(0)' : 'translateY(20px)',
                  transition: `opacity 0.5s ease ${idx *
                    0.07}s, transform 0.5s ease ${idx *
                    0.07}s, box-shadow 0.3s ease, translate 0.3s ease`,
                }}
              >
                <meta
                  itemProp="datePublished"
                  content={new Date(post.date).toISOString().split('T')[0]}
                />
                <meta itemProp="author" content={post.author} />
                <meta itemProp="keywords" content={post.tags.join(', ')} />

                <Link
                  to={`/blog/${post.slug}`}
                  className="block relative w-full h-48 overflow-hidden flex-shrink-0 cursor-pointer"
                >
                  <img
                    src={post.image}
                    alt={`${post.title} - Alliance Security Systems Blog`}
                    title={`${post.title} - ${post.category}`}
                    className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
                    itemProp="image"
                  />
                  <span
                    className={`absolute top-3 left-3 text-xs font-bold px-3 py-1 rounded-full ${
                      categoryColors[post.category] ??
                      'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {post.category}
                  </span>
                </Link>

                <div className="p-5 sm:p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-xs text-gray-400 mb-3">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      <time dateTime={new Date(post.date).toISOString().split('T')[0]}>
                        {post.date}
                      </time>
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      {post.readTime} min read
                    </span>
                  </div>

                  <Link to={`/blog/${post.slug}`} className="cursor-pointer">
                    <h4
                      className="text-base sm:text-lg font-bold text-[#0A1628] mb-3 leading-snug hover:text-[#D4A853] transition-colors line-clamp-2"
                      itemProp="headline"
                    >
                      {post.title}
                    </h4>
                  </Link>

                  <p
                    className="text-sm text-gray-500 leading-relaxed mb-5 flex-1 line-clamp-3 speakable"
                    itemProp="description"
                  >
                    {post.excerpt}
                  </p>

                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {post.tags.slice(0, 3).map((tag) => (
                      <span
                        key={tag}
                        className="text-[11px] bg-[#F8F9FA] text-gray-500 px-2.5 py-1 rounded-full border border-gray-100"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                        <img
                          src={post.authorImage}
                          alt={post.author}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-[#0A1628] leading-tight">
                          {post.author}
                        </p>
                        <p className="text-[11px] text-gray-400 leading-tight">
                          {post.authorRole}
                        </p>
                      </div>
                    </div>
                    <Link
                      to={`/blog/${post.slug}`}
                      className="flex items-center gap-1.5 text-[#D4A853] text-xs font-semibold hover:gap-2.5 transition-all cursor-pointer whitespace-nowrap"
                    >
                      Read More <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <nav
            className="flex items-center justify-center gap-2 mt-14"
            aria-label="Blog pagination"
          >
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-[#D4A853] hover:text-[#D4A853] disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
              aria-label="Previous page"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
              (page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-10 h-10 flex items-center justify-center rounded-full text-sm font-semibold transition-all cursor-pointer ${
                    currentPage === page
                      ? 'bg-[#D4A853] text-white shadow-md'
                      : 'border border-gray-200 text-gray-600 hover:border-[#D4A853] hover:text-[#D4A853]'
                  }`}
                  aria-label={`Page ${page}`}
                  aria-current={currentPage === page ? 'page' : undefined}
                >
                  {page}
                </button>
              )
            )}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-[#D4A853] hover:text-[#D4A853] disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
              aria-label="Next page"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </nav>
        )}
      </main>

      {/* CTA Banner */}
      <section
        className="bg-gradient-to-r from-[#0A1628] to-[#1a2d4a] py-14 sm:py-20 mx-4 sm:mx-6 rounded-2xl mb-10"
        aria-label="Free security assessment"
      >
        <div className="max-w-3xl mx-auto px-6 text-center">
          <span className="inline-block bg-[#D4A853] text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-wide mb-5 whitespace-nowrap">
            Free Security Assessment
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-4">
            Ready to Protect Your Property?
          </h2>
          <p className="text-white/70 text-base mb-8 max-w-xl mx-auto">
            Our certified security experts will assess your property and recommend
            the perfect solution — at no cost to you.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/contact"
              className="bg-[#D4A853] text-white px-8 py-3.5 rounded-full font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-lg"
            >
              Get Free Quote
            </Link>
            <a
              href="tel:18884589181"
              className="border-2 border-white/40 text-white px-8 py-3.5 rounded-full font-semibold hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap flex items-center gap-2"
            >
              <i className="ri-phone-line"></i> 1-888-458-9181
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#050B14] text-white pt-12 pb-8 mx-3 sm:mx-6 rounded-t-[24px] sm:rounded-t-[32px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 mb-10">
            <div>
              <div className="inline-block border-2 border-[#D4A853] rounded-lg px-4 py-2.5 bg-[#0A1628] mb-4">
                <div className="flex flex-col leading-none">
                  <span
                    className="text-xl font-extrabold tracking-wider text-[#D4A853]"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    ALLIANCE
                  </span>
                  <span className="text-[8px] font-semibold tracking-[0.25em] text-[#D4A853]">
                    SECURITY SYSTEMS
                  </span>
                </div>
              </div>
              <p className="text-white/60 text-sm mb-4">
                Trusted Security Since 1999
              </p>
              <a
                href="tel:18884589181"
                className="inline-block bg-[#D4A853] text-white px-5 py-2.5 rounded-full text-xs font-semibold hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>1-888-458-9181
              </a>
            </div>
            <div>
              <h3 className="text-sm font-bold mb-4 text-white">Quick Links</h3>
              <div className="space-y-2 text-sm">
                {[
                  { label: 'Home', path: '/' },
                  { label: 'Blog', path: '/blog' },
                  { label: 'FAQ', path: '/faq' },
                  { label: 'Service Areas', path: '/service-areas' },
                  { label: 'Contact Us', path: '/contact' },
                ].map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="block text-white/60 hover:text-[#D4A853] transition-colors cursor-pointer"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-bold mb-4 text-white">Head Office</h3>
              <div className="space-y-2 text-sm text-white/60">
                <p>2355 Derry Road East, Unit 1</p>
                <p>Mississauga, ON L5S 1V6</p>
                <a
                  href="tel:9056960613"
                  className="block hover:text-[#D4A853] transition-colors cursor-pointer"
                >
                  (905) 696-0613
                </a>
              </div>
            </div>
          </div>
          <div className="pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-white/40 text-xs">
              © 2024 Alliance Security Systems. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/privacy-policy"
                className="text-white/40 hover:text-white text-xs transition-colors cursor-pointer"
              >
                Privacy Policy
              </Link>
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white/40 hover:text-white text-xs transition-colors cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
